import { Button, Card, Spinner } from '@wundo/ui';
import type {
  NotificationDelivery,
  NotificationPreview,
  NotificationRecipient,
} from '@/lib/notificationSend.ts';
import { AdaptiveCardPreview } from '../AdaptiveCardPreview.tsx';
import { SlackBlocksPreview } from '../SlackBlocksPreview.tsx';
import { nameOf, skipLabel } from './sendLabels.ts';

const PREVIEW_NOTE =
  'Nothing was sent and no link was created. The links in this preview point at a placeholder address.';

function previewHint(isUserScope: boolean, takesRoleplay: boolean): string {
  if (!isUserScope) {
    return 'Pick a notification, a recipient and a meeting to see what that person would get.';
  }
  return takesRoleplay
    ? 'Pick the people, a roleplay, and tick who should get it.'
    : 'Pick a notification and tick the people who should get it.';
}

/* The backend refuses both anyway, but a button that has to be pressed to find that out lies. */
function sendBlockedReason(
  delivery: NotificationDelivery,
  resend: boolean,
): string | null {
  if (delivery.settled && !resend) return 'Already sent for this meeting.';
  if (delivery.channel === 'none') return 'This person cannot receive it.';
  return null;
}

function deliveryLine(
  delivery: NotificationDelivery,
  recipient: NotificationRecipient,
): string {
  const name = nameOf(recipient);

  switch (delivery.reason) {
    case 'msteams':
      return `${name} will receive this as a card in their Teams chat with Wundo.`;
    case 'slack':
      return `${name} will receive this as a message in their Slack DM with Wundo.`;
    case 'email':
      return `${name} will receive this by email at ${recipient.email ?? 'an unknown address'}.`;
    case 'no_teams_chat':
      return `${name} cannot receive this in Teams: they have no Teams chat with Wundo yet.`;
    case 'no_slack_chat':
      return `${name} cannot receive this in Slack: they have no Slack DM with Wundo yet.`;
    case 'no_webapp':
      return `${name} cannot receive this: their organisation does not use the web app this links to.`;
    case 'teams_only':
      return `${name} cannot receive this: it is only sent in the Teams chat, and they are on another channel.`;
    case 'teams_or_email_only':
      return `${name} cannot receive this: it is only sent in the Teams chat or by email, and they are on Slack.`;
    case 'no_email_address':
      return `${name} cannot receive this by email: they have no email address.`;
    case 'opted_out':
      return `${name} cannot receive this: they ${skipLabel('opted_out', recipient)}.`;
    case 'channel_none':
      return `${name} cannot receive this: their notification channel is set to none.`;
  }
}

interface PreviewSectionProps {
  canPreview: boolean;
  loading: boolean;
  preview: NotificationPreview | undefined;
  /** Whose preview is drawn; in a list they stand for everybody ticked. */
  recipient: NotificationRecipient | null;
  isUserScope: boolean;
  isEmail: boolean;
  takesRoleplay: boolean;
  resend: boolean;
  onSend: () => void;
}

export function PreviewSection(props: PreviewSectionProps) {
  const { preview, recipient, isUserScope, resend } = props;

  if (!props.canPreview) {
    return (
      <p className='text-sm text-ink-muted'>
        {previewHint(isUserScope, props.takesRoleplay)}
      </p>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      {props.loading && (
        <p role='status' className='flex items-center gap-2 text-sm text-ink-muted'>
          <Spinner className='size-4' />
          Building the preview...
        </p>
      )}

      {preview && recipient && (
        <>
          <DeliveryCard
            delivery={preview.delivery}
            recipient={recipient}
            isUserScope={isUserScope}
            isEmail={props.isEmail}
            resend={resend}
          />
          <PreviewRendering preview={preview} />
          {/* A list's send button sits on the list: one here would read as sending to this one person. */}
          {isUserScope ? (
            <p className='text-sm text-ink-muted'>{PREVIEW_NOTE}</p>
          ) : (
            <SingleSendButton
              blocked={sendBlockedReason(preview.delivery, resend)}
              onSend={props.onSend}
            />
          )}
        </>
      )}
    </div>
  );
}

interface DeliveryCardProps {
  delivery: NotificationDelivery;
  recipient: NotificationRecipient;
  isUserScope: boolean;
  isEmail: boolean;
  resend: boolean;
}

function DeliveryCard({
  delivery,
  recipient,
  isUserScope,
  isEmail,
  resend,
}: DeliveryCardProps) {
  return (
    <Card
      tone='quiet'
      className={delivery.channel === 'none' ? 'border-danger-line' : 'border-line'}
    >
      {/* A refused mail has a channel but goes nowhere; the line below says why. */}
      {!(isEmail && delivery.skip_reason) && (
        <p className='text-sm font-semibold text-ink'>
          {deliveryLine(delivery, recipient)}
        </p>
      )}
      {isUserScope ? (
        <ListDeliveryNotes delivery={delivery} recipient={recipient} />
      ) : (
        delivery.settled && (
          <p className='mt-1 text-sm text-ink-secondary'>
            {resend
              ? 'Already sent for this meeting. It goes out again, because you chose to also send to people who already received it.'
              : 'Already sent for this meeting. Wundo sends each notification at most once per person and meeting, so it will not go out again.'}
          </p>
        )
      )}
    </Card>
  );
}

function ListDeliveryNotes({
  delivery,
  recipient,
}: {
  delivery: NotificationDelivery;
  recipient: NotificationRecipient;
}) {
  return (
    <>
      {delivery.skip_reason && (
        <p className='mt-1 text-sm font-semibold text-ink first:mt-0'>
          {nameOf(recipient)} is left out when you send:{' '}
          {skipLabel(delivery.skip_reason, recipient)}.
        </p>
      )}
      {delivery.nothing_to_say && (
        <p className='mt-1 text-sm font-semibold text-ink'>
          There is nothing to tell {nameOf(recipient)} yet: they have no 1:1s with
          colleagues, so the send does nothing for them.
        </p>
      )}
      <p className='mt-1 text-sm text-ink-secondary'>
        Everybody else you ticked gets the same message, in their own language. Whoever is
        left out is named in the list above.
      </p>
    </>
  );
}

/*
 * Only the rendering that would actually arrive. The backend draws both, but a
 * mail next to a card reads as if this person gets both.
 */
function PreviewRendering({ preview }: { preview: NotificationPreview }) {
  const { channel, with_link: withLink } = preview.delivery;
  const inChat = channel === 'msteams' || channel === 'slack';

  return (
    <>
      {channel === 'msteams' && preview.teams_card && (
        <div className='max-w-xl'>
          <h3 className='mb-2 text-sm font-bold text-ink'>Teams card</h3>
          <AdaptiveCardPreview card={preview.teams_card} />
        </div>
      )}

      {channel === 'slack' && (
        <div className='max-w-xl'>
          <h3 className='mb-2 text-sm font-bold text-ink'>Slack message</h3>
          <SlackBlocksPreview blocks={preview.slack_blocks} />
        </div>
      )}

      {inChat && !withLink && (
        <p className='text-xs text-ink-muted'>
          No button — this organisation is not being steered to the web app.
        </p>
      )}

      {channel === 'email' && preview.email && (
        <div className='flex flex-col gap-2'>
          <h3 className='text-sm font-bold text-ink'>Email</h3>
          <p className='text-sm text-ink-muted'>Subject</p>
          <p className='text-sm font-semibold text-ink'>{preview.email.subject}</p>
          {/* In an iframe the mail's own styling cannot restyle the console; srcDoc keeps it off the network. */}
          <iframe
            srcDoc={preview.email.html}
            title='Email preview'
            className='h-144 w-full rounded-sm border border-line bg-surface'
          />
        </div>
      )}
    </>
  );
}

function SingleSendButton({
  blocked,
  onSend,
}: {
  blocked: string | null;
  onSend: () => void;
}) {
  return (
    <div className='flex items-center gap-3'>
      <Button disabled={blocked !== null} onClick={onSend}>
        Send now
      </Button>
      <span className='text-sm text-ink-muted'>{blocked ?? PREVIEW_NOTE}</span>
    </div>
  );
}
